import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/ReduxHooks';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { setInputBindingList } from '../../../redux/EditorSlice';
import type { InputBinding } from '../../../utils/inputBindings';
import {
  dedupeInputBindingsBySignature,
  getSupportedInputBindingsForButtonEditor,
  isSupportedInputBindingForButtonEditor,
} from '../../../utils/inputBindings';
import { editor as MonacoEditor } from 'monaco-editor';
import { replaceButtonInputBindingsInCode } from './controllerCodeUtils';

export function useMappingUi(device: 'controller' | 'drivebase', editor?: MonacoEditor.IStandaloneCodeEditor) {
  const dispatch = useAppDispatch();
  const bindingList = useAppSelector((state) => state.editor.inputBindingList);
  const bindingListRef = useRef<InputBinding[]>(bindingList);
  const hoveredGlobalButton = useAppSelector((state) => state.application.hoveredControllerButton);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogButton, setDialogButton] = useState<string>(device === 'drivebase' ? 'up' : 'a');
  const readOnly = useAppSelector((state) => state.project.loadedProject?.readOnly) ?? false;

  const mappingsByButton = useMemo(() => {
    const byButton: Record<string, InputBinding[]> = {};
    for (const item of bindingList) {
      if (item.device !== device) continue;
      const btn = String(item.button || '').toLowerCase();
      if (!byButton[btn]) byButton[btn] = [];
      byButton[btn].push(item);
    }
    return byButton;
  }, [bindingList, device]);

  const hasMapping = useCallback((btn: string) => (mappingsByButton[btn]?.length ?? 0) > 0, [mappingsByButton]);

  const getButtonFromEvent = (evt: React.MouseEvent<SVGSVGElement>) => {
    const element = evt.target as Element | null;
    const host = element && 'closest' in element ? (element as Element).closest('[data-button]') : null;
    const btn = host ? host.getAttribute('data-button') : null;
    return btn as string | null;
  };

  const handleSvgMouseMove = (evt: React.MouseEvent<SVGSVGElement>) => {
    const btn = getButtonFromEvent(evt);
    if (btn !== hoveredButton) {
      setHoveredButton(btn);
      dispatch(applicationSlice.actions.onHoverControllerButton(btn));
    }
  };

  const handleSvgMouseLeave = () => {
    if (hoveredButton !== null) {
      setHoveredButton(null);
      dispatch(applicationSlice.actions.onHoverControllerButton(null));
    }
  };

  const openDialogForButton = (btn: string) => {
    setDialogButton(btn);
    setDialogOpen(true);
  };

  const handleSvgClick = (evt: React.MouseEvent<SVGSVGElement>) => {
    const btn = getButtonFromEvent(evt);
    if (btn && !readOnly) openDialogForButton(btn);
  };

  const handleSaveButtonBindings = (button: string, nextBindings: InputBinding[]): boolean => {
    const currentBindingList = bindingListRef.current;
    const previousEditableBindings = getSupportedInputBindingsForButtonEditor(currentBindingList, device, button);
    const dedupedNextBindings = dedupeInputBindingsBySignature(nextBindings);
    if (editor) {
      const result = replaceButtonInputBindingsInCode(editor, previousEditableBindings, dedupedNextBindings);
      if (!result.ok) return false;
    }

    const next = [
      ...currentBindingList.filter((binding) => !isSupportedInputBindingForButtonEditor(binding, device, button)),
      ...dedupedNextBindings,
    ];
    dispatch(setInputBindingList(next));
    return true;
  };

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const nodes = svg.querySelectorAll('[data-button]');
    nodes.forEach((node) => {
      node.classList.add('controller-button');
      const btn = node.getAttribute('data-button') || '';
      const mapped = hasMapping(btn);
      const hovered = btn === hoveredButton || btn === hoveredGlobalButton;
      node.classList.toggle('mapped', mapped);
      node.classList.toggle('hovered', hovered);
    });
  }, [mappingsByButton, hoveredButton, hoveredGlobalButton, hasMapping]);

  useEffect(() => {
    bindingListRef.current = bindingList;
  }, [bindingList]);

  return {
    svgRef,
    readOnly,
    hoveredButton,
    dialogOpen,
    dialogButton,
    setDialogOpen,
    handleSvgMouseMove,
    handleSvgMouseLeave,
    handleSvgClick,
    handleSaveButtonBindings,
  } as const;
}
